import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import { TabStyles, TextFieldWidget, Tooltip } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { cssClass } from '~src/style';

import { FilePathValidationStatus } from '../../types/FilePathValidationStatus';

export type AddNewFileServerTextFieldProps = Readonly<{
  onFilePathChange: (domainName: string) => void;
  filePath: string;
  filePathValidationStatus: FilePathValidationStatus;
}>;

export type AddNewFileServerTextFieldState = Readonly<{
  fileServer?: string;
}>;

export function AddNewFileServerTextField(props: AddNewFileServerTextFieldProps) {
  const existingFileServer = props.filePath.split(/[\/\\]/)[0] || props.filePath;
  const [state, setState] = React.useState<AddNewFileServerTextFieldState>({fileServer: existingFileServer});
  const isValid = props.filePathValidationStatus === FilePathValidationStatus.NO_ERROR ? undefined : false;
  const errorMessage = getErrorMessageForFilePathValidationStatus(props.filePathValidationStatus);
  const filePathChanged = (value: string) => {
    value = value.replace(/\\/g, '/');
    props.onFilePathChange(value);
    const fileServer = value.split(/[\/\\]/)[0] || value;
    if (fileServer && fileServer.trim() !== '') {
      setState({fileServer: fileServer});
    }
  };

  return (<>
      <TextFieldWidget
          label={Messages.dataConnectSiteSettingsDomainFilePath()}
          onChange={e => filePathChanged(e.target.value)}
          onClear={() => filePathChanged('')}
          placeholder={Messages.dataConnectSiteSettingsDomainFilePathPlaceholder()}
          testId={'domain-fileserver-widget'}
          value={props.filePath}
          kind={'outline'}
          className={textFieldClass}
          style={{ width: '100%' }}
          valid={isValid}
          message={errorMessage}
        />
      {(props.filePathValidationStatus === FilePathValidationStatus.NO_ERROR
      || props.filePathValidationStatus === FilePathValidationStatus.NOT_UNIQUE_ERROR)
      && props.filePath && (
      <div className={fileServerTextClass}>
        <span>{Messages.dataConnectSiteSettingsDomainFileServer()}</span>
        <span style={{paddingLeft: 4, verticalAlign: 'text-top'}}>
        <Tooltip children={<NotificationInfoOutlineIcon size={14} alt={''} />}
          tooltipContent={
          renderTooltip(Messages.dataConnectSiteSettingsDomainFileServerToolTip())}
          position={Tooltip.defaultProps.position!}
          afterDelay={false}
          followCursor={false} />
        </span>
        <span>{': '+state.fileServer}</span>
      </div>
      )}
  </>
  );
}

function renderTooltip(text: string) {
  return <div >
    <span style={{display:'block'}}>{text}</span>
  </div>;
}

function getErrorMessageForFilePathValidationStatus(filePathValidationStatus: FilePathValidationStatus) {
  switch (filePathValidationStatus) {
    case FilePathValidationStatus.NO_ERROR:
      return '';
    case FilePathValidationStatus.INVALID_CHARACTERS_ERROR:
      return Messages.dataConnectSiteSettingsDomainValidationInvalidCharacters({
        DOMAIN_TYPE: Messages.dataConnectSiteSettingsDomainFilePath()
      });
    case FilePathValidationStatus.NOT_UNIQUE_ERROR:
      return Messages.dataConnectSiteSettingsDomainFileServerValidationNotUnique();
    case FilePathValidationStatus.FILESERVER_NAME_IS_DEFAULT:
      return Messages.dataConnectSiteSettingsDomainValidationDefault({
        DOMAIN_TYPE: Messages.dataConnectSiteSettingsDomainFilePath()
      });
    case FilePathValidationStatus.FILESERVER_NAME_IS_BLOCKED:
      return Messages.dataConnectSiteSettingsDomainValidationBlocked({
        DOMAIN_TYPE: Messages.dataConnectSiteSettingsDomainFilePath()
      });
    case FilePathValidationStatus.FILESERVER_NAME_MAXIMUM_CHARACTERS:
      return Messages.dataConnectSiteSettingsDomainValidationMaxCharacter({
        DOMAIN_TYPE: Messages.dataConnectSiteSettingsDomainFilePath()
      });
    case FilePathValidationStatus.INVALID_DASHES_ERROR:
      return Messages.dataConnectSiteSettingsDomainValidationInvalidDashes({
        DOMAIN_TYPE: Messages.dataConnectSiteSettingsDomainFilePath()
      });
    case FilePathValidationStatus.INVALID_UNDERSCORES_ERROR:
      return Messages.dataConnectSiteSettingsDomainValidationInvalidUnderscores({
        DOMAIN_TYPE: Messages.dataConnectSiteSettingsDomainFilePath()
      });
    case FilePathValidationStatus.INVALID_FORMAT_ERROR:
      return Messages.dataConnectSiteSettingsDomainValidationInvalidFilServerPath();
    default:
      return Messages.errorUnknown();
  }
}

const textFieldClass = cssClass('add-new-domain-text-field', 'root', {
  width: '100%',
  marginTop: TabStyles.Spacing.LargeSpace,
});

const fileServerTextClass = cssClass('add-new-domain-file-server-text', 'root', {
  width: '100%',
  marginTop: TabStyles.Spacing.SmallSpace,
});
